import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';

/**
 * Задание stylepack/option-description — тело действия
 * `setBlockOptionDescription`: описание ОДНОГО варианта ответа (вторая
 * строка под подписью). Своим действием, а не через `updateBlockOption`: тот
 * пишет подпись (label обязателен), и подмешивать туда второе поле значило бы
 * делать подпись необязательной у всех его вызывающих (довод WEB-1810, как у
 * `setBlockOptionIcon`).
 *
 * Пустое или из одних пробелов описание снимает КЛЮЧ (WEB-955): вариант без
 * описания равен варианту, которого правка не касалась. Состав списка не
 * меняется; нет такого варианта — состояние то же.
 *
 * Живёт здесь, а не в editorSlice.ts: фасад держит храповик длины (WEB-2552).
 * Создатель действия и регистрация — `./screenFieldActions.ts`, имя
 * `editor/setBlockOptionDescription`.
 */
export interface BlockOptionDescriptionPayload {
  screenId: string;
  blockId: string;
  optionId: string;
  description?: string;
}

export function setBlockOptionDescriptionReducer(
  state: EditorState,
  action: PayloadAction<BlockOptionDescriptionPayload>,
): EditorState {
  const { screenId, blockId, optionId, description } = action.payload;
  const keep = typeof description === 'string' && description.trim() !== '';
  let touched = false;
  const content = state.content.map((s) =>
    s.id !== screenId
      ? s
      : {
          ...s,
          blocks: s.blocks.map((b) =>
            b.id !== blockId
              ? b
              : {
                  ...b,
                  options: (b.options ?? []).map((o) => {
                    if (o.id !== optionId) return o;
                    touched = true;
                    if (!keep) {
                      const { description: _dropped, ...rest } = o;
                      return rest;
                    }
                    return { ...o, description };
                  }),
                },
          ),
        },
  );
  return touched ? { ...state, content, isDirty: true } : state;
}
